import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import Modal from '../components/Modal';
import TrainerConfigForm, { isTrainerConfigReady, useTrainerKindMeta } from '../components/TrainerConfigForm';

/**
 * Каталог тренажёров: свободный старт, выданные назначения, история.
 */
export default function TrainersPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [setupSlug, setSetupSlug] = useState(null);
  const [starting, setStarting] = useState(false);

  const { meta, loading: metaLoading, error: metaError, defaultConfig } =
    useTrainerKindMeta(setupSlug);
  const [config, setConfig] = useState({});

  useEffect(() => {
    if (defaultConfig && Object.keys(defaultConfig).length) {
      setConfig({ ...defaultConfig });
    }
  }, [defaultConfig]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catalogRes, assignRes, sessRes] = await Promise.all([
          api.get('/trainers/catalog/'),
          api.get('/trainers/assignments/').catch(() => ({ data: [] })),
          api.get('/trainers/sessions/').catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setItems(Array.isArray(catalogRes.data) ? catalogRes.data : []);
          setAssignments(Array.isArray(assignRes.data) ? assignRes.data : []);
          setSessions(Array.isArray(sessRes.data) ? sessRes.data : []);
        }
      } catch {
        if (!cancelled) setError('Не удалось загрузить тренажёры.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function startFree() {
    if (!setupSlug) return;
    setStarting(true);
    setError('');
    try {
      const res = await api.post('/trainers/sessions/start/', {
        kind: setupSlug,
        config,
      });
      navigate(`/trainers/sessions/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось начать.');
    } finally {
      setStarting(false);
    }
  }

  const setupItem = items.find((item) => item.slug === setupSlug);

  function closeSetup() {
    if (starting) return;
    setSetupSlug(null);
    setError('');
  }

  async function startAssignment(assignmentId) {
    setStarting(true);
    setError('');
    try {
      const res = await api.post(`/trainers/assignments/${assignmentId}/start/`);
      navigate(`/trainers/sessions/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось начать.');
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="practice-quizzes-page">
      <div className="catalog-page-header">
        <h1 className="catalog-page-title">Тренажёры</h1>
        <p className="catalog-page-lead">
          Короткие задания с генератором — отдельно от домашнего задания и тестов.
        </p>
      </div>

      {error && !setupSlug && (
        <div className="alert alert-error">{error}</div>
      )}

      {loading ? (
        <p className="text-secondary">Загрузка…</p>
      ) : (
        <>
          {assignments.length > 0 && (
            <section className="dashboard-section" style={{ marginBottom: '1.5rem' }}>
              <h2>Выдано вам</h2>
              <ul className="practice-quizzes-list">
                {assignments.map((a) => (
                  <li key={a.id} className="practice-quizzes-item">
                    <div className="practice-quizzes-item-main">
                      <h2 className="practice-quizzes-item-title">{a.display_title}</h2>
                      <p className="practice-quizzes-item-meta text-secondary">
                        {a.kind_title || a.kind}
                        {' · '}
                        {a.target_count} заданий
                        {a.has_finished_session ? ' · есть завершённая сессия' : ''}
                      </p>
                    </div>
                    <div className="practice-quizzes-item-actions">
                      {a.latest_session_id && (
                        <Link
                          to={`/trainers/sessions/${a.latest_session_id}`}
                          className="btn btn-outline btn-sm"
                        >
                          Разбор
                        </Link>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={starting}
                        onClick={() => void startAssignment(a.id)}
                      >
                        {starting ? '…' : 'Решать'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <ul className="practice-quizzes-list">
            {items.map((item) => (
              <li key={item.slug} className="practice-quizzes-item">
                <div className="practice-quizzes-item-main">
                  <h2 className="practice-quizzes-item-title">{item.title}</h2>
                  <p className="practice-quizzes-item-meta text-secondary">{item.blurb}</p>
                </div>
                <div className="practice-quizzes-item-actions">
                  {item.implemented ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        setError('');
                        setSetupSlug(item.slug);
                      }}
                    >
                      Настроить
                    </button>
                  ) : (
                    <span
                      className="btn btn-outline btn-sm"
                      style={{ pointerEvents: 'none', opacity: 0.7 }}
                    >
                      Скоро
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {sessions.length > 0 && (
            <section className="dashboard-section" style={{ marginTop: '1.5rem' }}>
              <h2>Недавние сессии</h2>
              <ul className="practice-quizzes-list">
                {sessions.slice(0, 10).map((s) => (
                  <li key={s.id} className="practice-quizzes-item">
                    <div className="practice-quizzes-item-main">
                      <h2 className="practice-quizzes-item-title">
                        {s.kind_title || s.kind}
                      </h2>
                      <p className="practice-quizzes-item-meta text-secondary">
                        {s.problems_correct}/{s.problems_answered} верно
                        {' · '}
                        {s.status === 'finished' ? 'завершена' : 'в процессе'}
                      </p>
                    </div>
                    <div className="practice-quizzes-item-actions">
                      <Link
                        to={`/trainers/sessions/${s.id}`}
                        className="btn btn-outline btn-sm"
                      >
                        Открыть
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <Modal
        open={Boolean(setupSlug)}
        title={meta?.title || setupItem?.title || 'Настройки'}
        onClose={closeSetup}
      >
        {setupItem?.blurb && (
          <p className="text-secondary" style={{ marginBottom: '0.75rem' }}>
            {setupItem.blurb}
          </p>
        )}
        {(error || metaError) && (
          <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>
            {error || metaError}
          </div>
        )}
        {metaLoading ? (
          <p className="text-secondary">Загрузка настроек…</p>
        ) : (
          <TrainerConfigForm meta={meta} config={config} onChange={setConfig} />
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={starting || metaLoading || !isTrainerConfigReady(meta, config)}
            onClick={() => void startFree()}
          >
            {starting ? '…' : 'Начать'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={starting}
            onClick={closeSetup}
          >
            Отмена
          </button>
        </div>
      </Modal>
    </div>
  );
}
